/**
 * useMonacoTableCompletion — shared Monaco IntelliSense hook for SQL/Python editors.
 *
 * Registers a completion provider that suggests table names and column names
 * from the workspace. Uses a model-check guard so multiple editors with the
 * same language don't interfere with each other.
 */
import { useEffect, useRef } from 'react';
import type { WorkspaceTable } from '../../../services/workspaceApi';

// Common pandas methods for Python completions
const PANDAS_METHODS = [
  'merge', 'groupby', 'filter', 'drop', 'rename', 'apply',
  'pivot_table', 'melt', 'fillna', 'dropna', 'sort_values',
  'head', 'tail', 'describe', 'value_counts', 'astype',
  'reset_index', 'set_index', 'to_csv', 'to_dict', 'copy',
  'loc', 'iloc', 'query', 'agg', 'transform', 'pipe',
  'join', 'concat', 'nunique', 'isnull', 'notnull',
  'str', 'dt', 'replace', 'map',
];

export function useMonacoTableCompletion(
  monacoInstance: any | null,
  editorInstance: any | null,
  tables: WorkspaceTable[],
  language: 'sql' | 'python',
  extraColumns?: string[],
): void {
  // Keep data in refs so the provider always sees the latest without re-registering
  const tablesRef = useRef(tables);
  const extraColumnsRef = useRef(extraColumns);
  tablesRef.current = tables;
  extraColumnsRef.current = extraColumns;

  const disposableRef = useRef<any>(null);

  useEffect(() => {
    if (!monacoInstance || !editorInstance) return;
    const monaco = monacoInstance;
    const editor = editorInstance;

    // Dispose previous registration for this editor
    disposableRef.current?.dispose();

    const triggerCharacters =
      language === 'python' ? ['.', ' ', '"', "'"] : ['.', ' '];

    disposableRef.current = monaco.languages.registerCompletionItemProvider(
      language,
      {
        triggerCharacters,
        provideCompletionItems: (model: any, position: any) => {
          // Only provide suggestions for this editor's model
          if (model !== editor.getModel()) return { suggestions: [] };

          const word = model.getWordUntilPosition(position);
          const range = {
            startLineNumber: position.lineNumber,
            endLineNumber: position.lineNumber,
            startColumn: word.startColumn,
            endColumn: word.endColumn,
          };

          const suggestions: any[] = [];
          const currentTables = tablesRef.current;
          const currentExtraCols = extraColumnsRef.current;

          // Collect all columns from all tables (deduplicated)
          const seenCols = new Set<string>();
          for (const tbl of currentTables) {
            for (const col of tbl.columns) {
              if (!seenCols.has(col.name)) {
                seenCols.add(col.name);
                if (language === 'python') {
                  suggestions.push({
                    label: col.name,
                    kind: monaco.languages.CompletionItemKind.Text,
                    insertText: col.name,
                    range,
                    detail: `column (${col.type})`,
                    sortText: `0_${col.name}`,
                  });
                } else {
                  suggestions.push({
                    label: col.name,
                    kind: monaco.languages.CompletionItemKind.Field,
                    insertText: col.name,
                    range,
                    detail: `column (${col.type})`,
                    sortText: `0_${col.name}`,
                  });
                }
              }
            }
          }

          // Extra columns (e.g. from query results in DataExplorerTab)
          if (currentExtraCols) {
            for (const col of currentExtraCols) {
              if (!seenCols.has(col)) {
                seenCols.add(col);
                suggestions.push({
                  label: col,
                  kind: language === 'python'
                    ? monaco.languages.CompletionItemKind.Text
                    : monaco.languages.CompletionItemKind.Field,
                  insertText: col,
                  range,
                  detail: 'column',
                  sortText: `0_${col}`,
                });
              }
            }
          }

          // Table name completions
          for (const tbl of currentTables) {
            suggestions.push({
              label: tbl.name,
              kind: language === 'python'
                ? monaco.languages.CompletionItemKind.Variable
                : monaco.languages.CompletionItemKind.Class,
              insertText: tbl.name,
              range,
              detail: 'table',
              sortText: `1_${tbl.name}`,
            });
          }

          // Python-specific: common pandas methods
          if (language === 'python') {
            for (const method of PANDAS_METHODS) {
              suggestions.push({
                label: method,
                kind: monaco.languages.CompletionItemKind.Function,
                insertText: method,
                range,
                detail: 'pandas',
                sortText: `2_${method}`,
              });
            }
          }

          return { suggestions };
        },
      },
    );

    return () => {
      disposableRef.current?.dispose();
      disposableRef.current = null;
    };
  }, [monacoInstance, editorInstance, language]);
}
